import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

/**
 * Core v1 schema alignment:
 *  - statement_transactions:   kind ('income'|'expense'), raw_text, matched_finance_id, match_status, match_score
 *  - reconcile_matches:        statement_txn_id, finance_entry_id, match_type, amount_diff, date_diff_days
 *  - "direction" (in/out) tồn tại CHỈ trong preview UI; khi import sẽ map sang kind.
 */

export type StatementDirection = "in" | "out";
export type StatementKind = "income" | "expense";

/** Preview-only row from /statement/parse (chưa ghi DB) */
export interface StagedRow {
  occurred_on: string | null;
  amount: number;
  direction: StatementDirection;  // preview only
  description: string | null;
  raw_text: string | null;        // Core v1 field name
}

/** Persisted row from statement_transactions (Core v1) */
export interface StatementTx {
  id: string;
  import_id: string;
  occurred_on: string | null;
  description: string | null;
  amount: number;
  kind: StatementKind;
  currency: string;
  raw_text: string | null;
  row_order: number | null;
  fingerprint: string | null;
  matched_finance_id: string | null;
  match_status: string | null;     // 'unmatched' | 'matched' | 'ignored' | ...
  match_score: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface FinanceLite {
  id: string; occurred_on: string; kind: StatementKind;
  category: string; amount: number; item_name: string | null; note: string | null;
}

export interface FinanceSuggestion {
  finance: FinanceLite;
  reason: string;
  score: number;
  amount_diff?: number;
  date_diff_days?: number | null;
}

export interface ReconcileMatchRow {
  statement_txn_id: string;
  finance_entry_id: string | null;
  status: string;
  match_type: string | null;
  amount_diff: number | null;
  date_diff_days: number | null;
  note: string | null;
  created_at: string;
}

export interface ReconcileRow {
  statement: StatementTx;
  match: ReconcileMatchRow | null;
  suggestions: FinanceSuggestion[];
  verdict: "missing_in_nana" | "review" | "auto_match_suggested" | "matched" | "ignored" | string;
}

export interface ReconcileReport {
  since: string;
  statement_count: number;
  finance_count: number;
  report: ReconcileRow[];
  finance_orphan: FinanceLite[];
}

export interface StatementImport {
  id: string;
  source: string;
  input_type: string;
  status: string;
  note: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
  /** Virtual — server tính tại runtime, không phải cột DB. */
  row_count: number;
}

export function useParseStatement() {
  return useMutation({
    mutationFn: (text: string) => api.post<{ rows: StagedRow[]; count: number }>("/statement/parse", { text }),
  });
}

export function useImportStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { source: string; raw_text: string; rows: StagedRow[] }) =>
      api.post<{ ok: true; import_id: string; count: number }>("/statement/import", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["statement_reconcile"] });
      qc.invalidateQueries({ queryKey: ["statement_imports"] });
    },
  });
}

export function useStatementImports() {
  return useQuery({
    queryKey: ["statement_imports"],
    queryFn: () => api.get<StatementImport[]>("/statement/imports"),
  });
}

export function useReconcileReport(days = 60) {
  return useQuery({
    queryKey: ["statement_reconcile", days],
    queryFn: () => api.get<ReconcileReport>("/statement/reconcile", { days }),
  });
}

export function useMarkMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { statement_txn_id: string; finance_entry_id: string; note?: string }) =>
      api.post<{ ok: true }>("/statement/match", p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["statement_reconcile"] }),
  });
}

export function useIgnoreTx() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { statement_txn_id: string; reason?: string }) =>
      api.post<{ ok: true }>("/statement/ignore", p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["statement_reconcile"] }),
  });
}

/**
 * Strict VND parser — không cho lưu mismatch.
 *   - "131150", "131.150", "131,150", "131.150₫" → 131150
 *   - "1.500.000", "1,500,000" → 1500000
 *   - "131.15" → AMBIGUOUS (reject)
 *   - "500k", "1.5tr" → vẫn hỗ trợ
 */
export type VndParseResult =
  | { ok: true; value: number; warning?: string }
  | { ok: false; reason: string };

export function parseVndStrict(input: string): VndParseResult {
  if (input == null) return { ok: false, reason: "Trống" };
  let s = String(input).trim().toLowerCase();
  if (s === "") return { ok: false, reason: "Trống" };
  s = s.replace(/[\s₫đ]/g, "");
  const sm = s.match(/(k|tr|m|b)$/);
  if (sm) {
    const mul = sm[1] === "k" ? 1_000 : sm[1] === "b" ? 1_000_000_000 : 1_000_000;
    const body = s.slice(0, -sm[1].length).replace(",", ".");
    const n = Number(body);
    if (!isFinite(n) || n <= 0) return { ok: false, reason: "Số không hợp lệ" };
    return { ok: true, value: Math.round(n * mul) };
  }
  if (/^\d+$/.test(s)) return { ok: true, value: Number(s) };
  const dots = (s.match(/\./g) || []).length;
  const commas = (s.match(/,/g) || []).length;
  const single = s.match(/^(\d{1,3})([.,])(\d{1,2})$/);
  if (single) {
    return { ok: false, reason: `Số "${input}" mơ hồ: không rõ là ${single[1]}${single[3]} (rút gọn) hay ${single[1]}.${single[3]} (thập phân). Nhập rõ, ví dụ ${single[1]}${single[3]}00 hoặc ${single[1]}.${single[3]}00.` };
  }
  const triple = s.match(/^(\d{1,3})([.,])(\d{3})$/);
  if (triple) {
    return { ok: true, value: Number(triple[1] + triple[3]), warning: `Hiểu "${input}" = ${triple[1]}${triple[3]}. Nếu định nhập 1.5 (thập phân), hãy ghi 1${triple[2]}50.` };
  }
  if (dots + commas >= 1) {
    const lastDot = s.lastIndexOf(".");
    const lastComma = s.lastIndexOf(",");
    const lastSep = Math.max(lastDot, lastComma);
    const tail = s.slice(lastSep + 1);
    if (tail.length === 3) {
      const cleaned = s.replace(/[.,]/g, "");
      if (!/^\d+$/.test(cleaned)) return { ok: false, reason: "Số không hợp lệ" };
      return { ok: true, value: Number(cleaned) };
    }
    if (tail.length === 1 || tail.length === 2) {
      return { ok: false, reason: `Phần thập phân ${tail} đáng nghi với VND. Nếu là rút gọn hãy thêm '00'.` };
    }
  }
  return { ok: false, reason: "Số không hợp lệ" };
}
